import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ModalOptions, ModalService } from '@healthcatalyst/cashmere';
import { OktaAuthStateService } from '@okta/okta-angular';
import { AuthState } from '@okta/okta-auth-js';
import { Observable, distinctUntilChanged, filter, forkJoin, map, startWith, take, tap } from 'rxjs';
import { ToastNotificationService } from 'src/app/core/services/toast-notification.service';
import { AccountService } from 'src/app/modules/data/data-entry/services/account.service';
import * as tooltips from 'src/app/shared/constants/tooltips';
import { clearLocalStorage } from 'src/app/shared/utils/local-storage.utils';
import { clearSessionStorage } from 'src/app/shared/utils/session-storage.utils';
import { TenantUserRolePermissionsService } from '../core/services/application/tenant-user-role-permissions.service';
import { OktaAuthService } from '../core/services/http/okta-auth.service';
import { TenantPermissionsSwitchService } from '../modules/data/data-entry/services/tenant-permissions-switch/tenant-permissions-switch.service';
import BaseComponent from '../shared/components/base.component';
import {
    accessGroupsView,
    accessManagementView,
    accessUsersView,
    appsView,
    cobrandingView,
    dataEntryView,
    monitoringView,
    promotionsView,
    reportingView,
    schedulesView,
    sourcesView
} from '../shared/constants/permissions';
import { Tenant } from '../shared/models/tenant';
import { TenantContextService } from '../shared/tenant-context/tenant-context.service';
import { AboutModalComponent } from './about-modal.component';
import { ClockService } from './clock.service';
import { ReleaseNotesModalService } from './release-notes-modal.service';
import { UserDetailModalComponent } from './user-detail-modal/user-detail-modal.component';
import { FeatureToggleService } from '../core/services/application/feature-toggle.service';
import { FeatureFlag } from '../shared/constants/feature-flags';
import { ReleaseNotesService } from '../core/services/application/release-notes.service';
import { dbxSessionStorageKey } from '../shared/constants/constants';

@Component({
    selector: 'cdp-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
    standalone: false
})
export class NavbarComponent extends BaseComponent implements OnInit {
    tooltips = tooltips;
    public hasAnyTenantAdminPermissions$: Observable<boolean>;
    public hasDataTabPermission$: Observable<boolean>;
    public hasPlatformTabPermission$: Observable<boolean>;
    public hasHomeTabPermission$: Observable<boolean>;
    public get showingClock(): boolean {
        return this.clockService?.showingClock || false;
    }
    public get tenantCode(): string {
        return this.tenantContextService.currentTenant?.tenantId || '—';
    }
    public get tenantCobrandImg(): string | undefined {
        return this.tenantContextService.currentTenant?.fileContents;
    }
    public get tenantSwitcherTooltip(): string {
        return this.tenantContextService.currentTenant
            ? `Change tenant (${this.tenantContextService.currentTenant?.name})`
            : 'Select a tenant';
    }
    public get showTenantSwitcher(): boolean {
        return this.tenantContextService.showTenantSwitcher;
    }

    public get tenantIsInactive$(): Observable<boolean> {
        return this.tenantContextService.tenantSelected$.pipe(
            map((tenant: Tenant | undefined) => {
                return !!tenant?.tenantId && !tenant?.activeFlag;
            }),
            startWith(false)
        );
    }

    public get enableAdminTabForGlobal(): boolean {
        return this.oktaAuth.isGlobalAdmin();
    }

    public get defaultLandingTabName(): string {
        return 'Home';
    }

    get showDbxClearSessionButton(): boolean {
        return (
            this.featureToggleService.getFeatureFlagsByName(FeatureFlag.UseModifiedDBXLink).enabled &&
            !!sessionStorage.getItem(dbxSessionStorageKey)?.length
        );
    }

    public name$!: Observable<string>;
    public userInfo: AuthState;

    public clearUserSession(): void {
        this.accountService
            .clearUserCache()
            .then(() => {
                clearLocalStorage();
                clearSessionStorage();
                this.toast.showSuccess(`User Session Cleared`);
            })
            .catch(() => {
                this.toast.showError(
                    `An error occurred while attempting to clear the user session`,
                    'Clear User Session Failed'
                );
            });
    }

    constructor(
        private modalService: ModalService,
        private _oktaAuthStateService: OktaAuthStateService,
        private oktaAuth: OktaAuthService,
        public releaseNotesModalService: ReleaseNotesModalService,
        private tenantContextService: TenantContextService,
        private sanitizer: DomSanitizer,
        private clockService: ClockService,
        private accountService: AccountService,
        private toast: ToastNotificationService,
        private tenantRolePermissionService: TenantUserRolePermissionsService,
        private tenantPermissionsSwitchService: TenantPermissionsSwitchService,
        private featureToggleService: FeatureToggleService,
        private releaseNotesService: ReleaseNotesService
    ) {
        super();
    }

    ngOnInit(): void {
        this.name$ = this._oktaAuthStateService.authState$.pipe(
            this.unsubscribeOnDestroy(),
            filter((authState: AuthState) => !!authState && !!authState.isAuthenticated),
            tap((authState: AuthState) => {
                this.userInfo = authState;
                this.tenantRolePermissionService.initTenantRolePermissions();
                this.tenantPermissionsSwitchService.initTenantSystemPermissions();

                this.checkForNewReleaseNotes();
            }),
            map((authState: AuthState) => authState.idToken?.claims.name ?? '')
        );
        this.tenantContextService.tenantSelected$
            .pipe(distinctUntilChanged(), this.unsubscribeOnDestroy())
            .subscribe(tenant => {
                if (tenant) {
                    this.hasAnyTenantAdminPermissions$ = this.enableAdminTabForTenant();
                    this.hasDataTabPermission$ = this.hasDataTabPermission();
                    this.hasPlatformTabPermission$ = this.hasPlatformTabPermission();
                    this.isGlobalAdmin = this.oktaAuth.isGlobalAdmin();
                    this.hasHomeTabPermission$ = this.hasHomeTabPermission();
                } else {
                    this.isGlobalAdmin = this.oktaAuth.isGlobalAdmin();
                }
            });
    }

    enableAdminTabForTenant(): Observable<boolean> {
        return forkJoin([
            this.tenantPermissionsSwitchService.getLatestTenant().pipe(take(1)),
            this.tenantRolePermissionService.hasRequiredUserPermissions([cobrandingView]).pipe(take(1)),
            this.tenantRolePermissionService.hasRequiredUserPermissions([accessManagementView]).pipe(take(1)),
            this.tenantRolePermissionService.hasRequiredUserPermissions([dataEntryView]).pipe(take(1)),
            this.tenantRolePermissionService.hasRequiredUserPermissions([accessUsersView]).pipe(take(1)),
            this.tenantRolePermissionService.hasRequiredUserPermissions([accessGroupsView]).pipe(take(1))
        ]).pipe(
            map(
                ([
                    systemPermissions,
                    hasCobranding,
                    hasAccessManagement,
                    hasDataEntry,
                    hasAccessViewer,
                    hasGroupsView
                ]) => {
                    return !!(
                        hasCobranding ||
                        hasAccessManagement ||
                        hasAccessViewer ||
                        hasGroupsView ||
                        (hasDataEntry && systemPermissions?.canManageUsers)
                    );
                }
            )
        );
    }

    hasDataTabPermission(): Observable<boolean> {
        return forkJoin([
            this.tenantRolePermissionService.hasRequiredUserPermissions([dataEntryView]).pipe(take(1)),
            this.tenantRolePermissionService.hasRequiredUserPermissions([reportingView]).pipe(take(1))
        ]).pipe(
            map(([dataEntryPermission, reportingViewPermission]) => {
                return dataEntryPermission || reportingViewPermission;
            })
        );
    }

    hasPlatformTabPermission(): Observable<boolean> {
        const requiredPermissions = [sourcesView, schedulesView, monitoringView, promotionsView];
        return this.tenantRolePermissionService.hasAnyRequiredUserPermissions(requiredPermissions);
    }

    hasHomeTabPermission(): Observable<boolean> {
        const requiredPermissions = [appsView];
        return this.tenantRolePermissionService.hasAnyRequiredUserPermissions(requiredPermissions);
    }

    openAbout() {
        this.modalService.open(AboutModalComponent, { size: 'md' });
    }

    openReleaseNotes() {
        // The service will return cached data unless cache was cleared
        this.releaseNotesService
            .getReleaseNotes()
            .pipe(take(1))
            .subscribe({
                next: async releaseNotes => {
                    await this.releaseNotesModalService.transformAndOpenAdminReleaseNotes(releaseNotes);
                }
            });
    }

    private checkForNewReleaseNotes(): void {
        this.releaseNotesService
            .getReleaseNotes()
            .pipe(take(1))
            .subscribe({
                next: releaseNotes => {
                    this.releaseNotesModalService.checkForNewAdminReleaseNotes(releaseNotes);
                }
            });
    }

    toggleClock(): void {
        this.clockService.toggleClock();
    }

    async logout() {
        await this.oktaAuth.signOut();
        this.tenantContextService.clearTenantContext();
    }

    sanitize(url: string): SafeUrl {
        return this.sanitizer.bypassSecurityTrustUrl(url);
    }

    openUserDetail() {
        const modalOptions: ModalOptions = {
            ignoreOverlayClick: true,
            size: 'lg',
            data: this.userInfo?.idToken?.claims
        };
        this.modalService.open(UserDetailModalComponent, modalOptions);
    }

    clearDbxSession() {
        sessionStorage.removeItem(dbxSessionStorageKey);
    }
}
